import React, { useMemo, useState } from "react";
import { Field, reduxForm } from "redux-form";
import { connect } from "react-redux";
import { isRolePlatformAdmin, token } from "../../modules/auth";
import { renderFormInput } from "../../modules/formTools";
import { required } from "../../modules/validation";
import Loading from "../Loading";
import { ButtonPrimary } from "../Button";
import { Input, Select,Toggle } from "../Form";
import { createCompany, createCompanyWithUser } from "../../redux/actions";
const v = {
  required: required(),
};

function MinimalCompanyForm({
  countries,
  industries,
  fullWidth = false,
  isRolePlatformAdmin,
  isRoleEdgeAdmin,
  handleSubmit,
  submitting,
  createCompany,
  createCompanyWithUser,
  includeUserForm = false,
  token,
  agencyId,
  onSuccess,
  companyDomains,
  enquiryId = false
}) {
  const [approvedDomainsWarning, setApprovedDomainsWarning] = useState(null);
  const onSubmit = async formValues => {
    formValues.agency_id = agencyId;
    if(enquiryId){
      formValues.enquiry_id = enquiryId;

    }
    try {
      const response = includeUserForm
        ? await createCompanyWithUser(formValues, token)
        : await createCompany(formValues, token);
      if (onSuccess) {
        onSuccess(response);
      }

      return response;
    } catch (error) {
      throw error;
    }
  };

  const handleApprovedDomains = e => {
    const value = e.currentTarget.value;
    if (!value?.includes?.("@")) return;
    const emailDomain = value.split("@")[1];
    const matchingDomain = companyDomains.find(
      company =>
        company.approved_domains && company.approved_domains === emailDomain
    );

    setApprovedDomainsWarning(
      matchingDomain
        ? `Are you sure? One of the domains listed is in use beneath a different Company: ${matchingDomain.name}`
        : null
    );
  };

  const countriesOptions = useMemo(() => {
    const sortedCountries = [...countries].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    const ukIndex = sortedCountries.findIndex(
      opt => opt.name === "United Kingdom"
    );
    if (ukIndex > -1) {
      const [uk] = sortedCountries.splice(ukIndex, 1);
      sortedCountries.unshift(uk);
    }

    return sortedCountries.map(country => ({
      text: country.name,
      value: country.id,
    }));
  }, [countries]);

  const industriesOptions = useMemo(
    () =>
      (industries || [])
        .filter(ind => isRolePlatformAdmin || !ind.data.admin_only)
        .map(ind => ({ text: ind.data.name, value: ind.data.id })),
    [industries, isRolePlatformAdmin]
  );

  return (
    <>
      <div className="overflow-hidden border-gray-200 sm:rounded-lg">
        <form className="border-none" onSubmit={handleSubmit(onSubmit)}>
          <div className="row">
            <div
              className={`col col-w-full mb-6 ${
                fullWidth ? "" : "md:col-w-1/2"
              }`}
            >
              <div className="flex flex-wrap -mx-3">
                <div className="w-full px-3 mb-4">
                  <Field
                    name="name"
                    label="Company Name"
                    component={renderFormInput}
                    validate={v.required}
                  />
                </div>
                <div className="w-full md:w-1/2 px-3 mb-4">
                  <Field
                    name="address1"
                    label="Address Line 1"
                    component={renderFormInput}
                    validate={v.required}
                  />
                </div>
                <div className="w-full md:w-1/2 px-3 mb-4">
                  <Field
                    name="address2"
                    label="Address Line 2"
                    component={renderFormInput}
                  />
                </div>
                <div className="w-full md:w-1/2 px-3 mb-4">
                  <Field
                    name="address3"
                    label="Address Line 3"
                    component={renderFormInput}
                  />
                </div>
                <div className="w-full md:w-1/2 px-3 mb-4">
                  <Field
                    name="city"
                    label="City"
                    component={renderFormInput}
                    validate={v.required}
                  />
                </div>
                <div className="w-full md:w-1/2 px-3 mb-4">
                  <Field
                    name="county"
                    label="County"
                    component={renderFormInput}
                    validate={v.required}
                  />
                </div>
                <div className="w-full md:w-1/2 px-3 mb-4">
                  <Field
                    name="postcode"
                    label="Postcode"
                    component={renderFormInput}
                    validate={v.required}
                  />
                </div>
                <div className="w-full md:w-1/2 px-3 mb-4">
                  <Field
                    name="country_id"
                    label="Country"
                    component={Select}
                    options={countriesOptions}
                    validate={v.required}
                  />
                </div>
                <div className="w-full md:w-1/2 px-3 mb-4">
                  <Field
                    name="industry_id"
                    label="Industry Sector"
                    component={Select}
                    options={industriesOptions}
                    validate={!isRoleEdgeAdmin ? v.required : null}
                  />
                </div>
                <div className="w-full md:w-1/2 px-3 mb-4">
                  <Field
                    name="email_address"
                    label="General Email Address"
                    component={renderFormInput}
                    validate={v.email}
                  />
                </div>
                <div className="w-full md:w-1/2 px-3 mb-4">
                  <Field
                    name="phone_number"
                    label="General Phone Number"
                    component={renderFormInput}
                  />
                </div>
                <div className="w-full md:w-1/2 px-3 mb-4">
                  <Field
                    name="website_address"
                    label="Website Address"
                    component={renderFormInput}
                  />
                </div>
              </div>
              {includeUserForm ? (
                <div className="mt-8">
                  <h4 className="mb-4 font-semibold text-lg">Client Details</h4>
                  <div className="flex flex-wrap -mx-3">
                    <div className="w-full md:w-1/2 px-3 mb-4">
                      <Field
                        name="first_name"
                        label="First name"
                        validate={v.firstName}
                        component={Input}
                      />
                    </div>
                    <div className="w-full md:w-1/2 px-3 mb-4">
                      <Field
                        name="surname"
                        label="Surname"
                        validate={v.surname}
                        component={Input}
                      />
                    </div>
                    <div className="w-full md:w-1/2 px-3 mb-4">
                      <Field
                        name="email"
                        type="email"
                        label="Email address"
                        component={Input}
                        onChange={e => handleApprovedDomains(e)}
                      />
                      {approvedDomainsWarning ? (
                        <div className={"my-2"}>
                          <span className="text-warning font-normal">
                            {approvedDomainsWarning}
                          </span>
                        </div>
                      ) : null}
                    </div>
                    <div className="w-full md:w-1/2 px-3 mb-4">
                      <Field
                        name="phone"
                        label="Phone"
                        validate={v.phone}
                        component={Input}
                      />
                    </div>
                  </div>
                </div>
              ) : null}

              <ButtonPrimary type="submit">
                {submitting ? <Loading inline /> : "Save"}
              </ButtonPrimary>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}

const mapStateToProps = (state, props) => {
  let initialValues = {};
  if (Object.entries(props.company).length > 0) {
    initialValues = {
      ...props.company,
      ...props.company.address,
    };
  }

  if (props.agencyCode) {
    initialValues.agency_code = props.agencyCode;
  }

  return {
    initialValues,
    token: token(state),
    isRolePlatformAdmin: isRolePlatformAdmin(state),
  };
};

const mapDispatchToProps = {
  createCompany,
  createCompanyWithUser,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  reduxForm({
    form: "minimalCompanyForm",
    enableReinitialize: true,
    destroyOnUnmount: false,
  })(MinimalCompanyForm)
);
